import type { ExactRateDTO, GiftcardRateDTO } from '../contracts/types.js';

/**
 * Rate formatting and price validation for the console.
 *
 * A rate arrives as an exact rational — `{ numerator, denominator }`, both base-10 integer
 * strings — because that is how the API and the ledger hold it: `1 / 1712.40` has no finite
 * decimal expansion, so anything that inverts or composes rates has to keep them rational.
 * The console's only jobs are to render one as a decimal an operator can read and edit, and
 * to check that what they typed is a price the API will accept.
 *
 * **Nothing here calls `Number`, `parseFloat`, or does decimal arithmetic.** Rendering is
 * `BigInt` long division; validation is a regular expression over the characters typed.
 * The string the operator entered travels to the API verbatim and is turned into a rational
 * there by `Rate.fromDecimal`, which is itself pure string surgery over bigints. So between
 * the keystroke and the `decimal(65,0)` columns the value is only ever a decimal string or
 * a pair of bigints — it is never briefly a float, which is the one way a kobo goes missing.
 *
 * The console cannot import from `@genex/contracts` at runtime (types only — see
 * `contracts/types.ts`), so the rules below mirror `rateOverridePriceSchema` and
 * `RATE_OVERRIDE_MAX_DECIMALS`. The API is still the enforcement authority; this copy only
 * spares the operator a round-trip to be told they typed a comma.
 */

/** Mirrors the contract's `RATE_OVERRIDE_MAX_DECIMALS`. */
export const RATE_MAX_DECIMALS = 8;

/**
 * Decimals always shown, even when the rate is round. Two, so a naira price reads as a
 * naira price (`1,712.40`) rather than as an integer that happens to have no kobo.
 */
const RATE_MIN_DECIMALS = 2;

/** Mirrors `rateOverridePriceSchema`: a plain positive decimal, bounded fraction. */
const PRICE_SHAPE = new RegExp(String.raw`^(?:0|[1-9]\d*)(?:\.\d{1,${RATE_MAX_DECIMALS}})?$`);

function pow10(exponent: number): bigint {
  return 10n ** BigInt(exponent);
}

/**
 * `numerator / denominator` as a plain decimal string, rounded half-up at
 * `RATE_MAX_DECIMALS` and trimmed to at least `RATE_MIN_DECIMALS`.
 *
 * The division is `(2·numerator·10^d + denominator) / (2·denominator)`, which is exact
 * half-up rounding in integer arithmetic — the doubling is what lets the tie land up
 * without ever leaving `BigInt`.
 *
 * Rounding at all is a display concession: a rational like `1/3` has no decimal form. It
 * costs nothing in practice because a price the operator typed has at most eight decimals
 * by construction, so it renders back exactly as they entered it.
 */
export function rateToInput(rate: ExactRateDTO): string {
  const numerator = BigInt(rate.numerator);
  const denominator = BigInt(rate.denominator);
  if (denominator === 0n) return '0';

  const scaled = (2n * numerator * pow10(RATE_MAX_DECIMALS) + denominator) / (2n * denominator);
  const digits = scaled.toString().padStart(RATE_MAX_DECIMALS + 1, '0');
  const whole = digits.slice(0, digits.length - RATE_MAX_DECIMALS);
  let frac = digits.slice(digits.length - RATE_MAX_DECIMALS);

  while (frac.length > RATE_MIN_DECIMALS && frac.endsWith('0')) frac = frac.slice(0, -1);
  return `${whole}.${frac}`;
}

/** The same value with thousands separators, for read-only display. Not re-parseable. */
export function formatRate(rate: ExactRateDTO): string {
  const plain = rateToInput(rate);
  const dot = plain.indexOf('.');
  const whole = plain.slice(0, dot);
  return `${whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}${plain.slice(dot)}`;
}

/** `1 USDT = ₦1,712.40` — a whole board row rendered as one line. */
export function formatPairPrice(rate: ExactRateDTO, base: string, quote: string): string {
  const symbol = quote === 'NGN' ? '₦' : '';
  return `1 ${base} = ${symbol}${formatRate(rate)}${symbol ? '' : ` ${quote}`}`;
}

export type PriceCheck =
  | { readonly ok: true; readonly price: string }
  | { readonly ok: false; readonly message: string };

/**
 * Validate a typed price against the same rules the contract applies, and hand back the
 * trimmed string to send. A pure check — it never converts the value to a number, so there
 * is nothing here that could round what the operator typed before the API sees it.
 */
export function parseRateInput(input: string): PriceCheck {
  const price = input.trim();
  if (price === '') return { ok: false, message: 'Enter a price.' };
  if (price.length > 40) return { ok: false, message: 'That price is too long.' };
  if (!PRICE_SHAPE.test(price)) {
    return {
      ok: false,
      message: `Use a plain decimal with up to ${RATE_MAX_DECIMALS} decimal places, e.g. 1750.25`,
    };
  }
  // `0`, `0.00` and friends match the shape but are not a price, and a zero rate would make
  // every quote against it round to nothing.
  if (!/[1-9]/.test(price)) return { ok: false, message: 'The price must be more than zero.' };
  return { ok: true, price };
}

/**
 * Whether two exact rationals are the same number.
 *
 * Cross-multiplied rather than compared field by field, because `3502/2` and `1751/1` are
 * the same price and the API stores rationals in lowest terms — so a field-wise comparison
 * would report a change the operator did not make.
 */
export function sameRate(a: ExactRateDTO, b: ExactRateDTO): boolean {
  return BigInt(a.numerator) * BigInt(b.denominator) === BigInt(b.numerator) * BigInt(a.denominator);
}

// ── Gift-card rates ───────────────────────────────────────────────────────────────
//
// A gift-card rate is the same *kind* of value as an FX override — a decimal an operator
// types, carried verbatim to the API and turned into an exact rational there — with one
// difference that changes every formula below: it is stored in **kobo per one whole unit
// of face value**, while the operator thinks and types in **naira**. So the stored
// rational is a hundred times the number on screen, and every conversion between the two
// has to say which side of that factor it is on.
//
// `giftcardRatePriceSchema` in the contract is deliberately the same shape and the same
// eight-decimal bound as `rateOverridePriceSchema`, so `parseRateInput` above validates a
// gift-card rate correctly and is reused rather than copied. Only the arithmetic differs.

/** Kobo in one naira. Named rather than written as `100n` wherever the factor appears. */
const KOBO_PER_NAIRA = 100n;

/**
 * The stored rational rendered back as the naira decimal an operator typed.
 *
 * `numerator` is kobo, so dividing by `denominator × 100` gives naira per whole face
 * unit — which is exactly `rateToInput` over a denominator scaled by a hundred, so the
 * one piece of long division in this file stays in one place. A rate the operator entered
 * has at most eight decimals by construction, so this round-trips their keystrokes exactly.
 */
export function giftcardRateToInput(rate: GiftcardRateDTO): string {
  return rateToInput({
    numerator: rate.numerator,
    denominator: (BigInt(rate.denominator) * KOBO_PER_NAIRA).toString(),
  });
}

/**
 * The exact rational a typed gift-card rate becomes — the console's mirror of the API's
 * `parseGiftcardRate`.
 *
 * Pure string surgery over bigints: strip the point, count the fraction digits, raise ten
 * to that power, then multiply into kobo. No `Number`, no `parseFloat`, no intermediate
 * float — the same discipline `rateToInput` holds to.
 *
 * This does **not** replace the API's parse and the result is never sent: the *string* the
 * operator typed is what travels, and the server derives its own rational from it. What
 * this is for is the two things the console can only do locally — showing the payout a
 * rate implies before it is saved, and warning about a ten-fold slip before the API
 * refuses one. Deriving those from the same arithmetic the server uses is what makes the
 * preview a preview rather than a guess.
 */
export function giftcardRateRational(price: string): { numerator: bigint; denominator: bigint } | null {
  const checked = parseRateInput(price);
  if (!checked.ok) return null;

  const dot = checked.price.indexOf('.');
  const whole = dot === -1 ? checked.price : checked.price.slice(0, dot);
  const frac = dot === -1 ? '' : checked.price.slice(dot + 1);

  const numerator = BigInt(whole + frac) * KOBO_PER_NAIRA;
  const denominator = pow10(frac.length);
  // Reduced, because the API reduces — `"1200.00"` and `"1200"` must be *the same rational*,
  // not two spellings of one. Nothing downstream here would notice the difference (the payout
  // divides and the deviation guard cross-multiplies, so both are scale-invariant), but this
  // function claims to produce what the server stores, and an unreduced pair would make that
  // claim false the moment anyone compared one against a value read back from the API.
  const divisor = gcd(numerator, denominator);
  return { numerator: numerator / divisor, denominator: denominator / divisor };
}

/** Greatest common divisor over bigints, for reducing a rational to lowest terms. */
function gcd(a: bigint, b: bigint): bigint {
  let x = a;
  let y = b;
  while (y !== 0n) [x, y] = [y, x % y];
  return x === 0n ? 1n : x;
}

/**
 * The naira payout, in kobo, for one face value at a rate — the console's mirror of the
 * API's `computePayout`, and the number an operator setting a rate actually cares about.
 *
 *   payoutKobo = rateNumerator · faceMinor · quantity / (rateDenominator · 10^decimals)
 *
 * Floored, exactly as the server floors it, because that is the house-safe direction for
 * money leaving the house — and a preview that rounded the other way would show a figure a
 * kobo above what the customer will actually be paid.
 */
export function giftcardPayoutKobo(input: {
  readonly rate: { readonly numerator: bigint; readonly denominator: bigint };
  /** Face value in minor units of the face currency (US cents, pence…). */
  readonly faceMinor: bigint;
  /** Minor-unit decimals of the face currency. Every supported face fiat is 2. */
  readonly faceDecimals: number;
  readonly quantity: bigint;
}): bigint {
  const scale = pow10(input.faceDecimals);
  const numerator = input.rate.numerator * input.faceMinor * input.quantity;
  const denominator = input.rate.denominator * scale;
  if (denominator === 0n) return 0n;
  // Both sides are non-negative here (the schema refuses a negative rate and a negative
  // face), so truncating division *is* the floor.
  return numerator / denominator;
}

/**
 * How far a single write may move a brand's rate before the API refuses it: **ten times**,
 * in either direction, anchored on the brand's own standing rate.
 *
 * Mirrors `GIFTCARD_RATE_MAX_CHANGE_FACTOR` in the API. Ten is not arbitrary — the mistake
 * the guard exists for is a misplaced decimal point or a rate typed in kobo, and every one
 * of those is a factor of ten or more.
 */
export const GIFTCARD_RATE_MAX_CHANGE_FACTOR = 10n;

/**
 * Whether a proposed rate is inside the band the API will accept, given the standing one.
 *
 * Cross-multiplied rather than divided, so the comparison is exact integers. The band is
 * **exclusive** — a move of exactly ten times is refused — because a misplaced decimal
 * point is *precisely* 10×, and a check that allowed the boundary would wave through the
 * single most likely version of the error it was written for. Copied that way deliberately:
 * a console that warned at 10.01× would let the operator press Save and then be refused.
 *
 * Only meaningful for an edit. Creating a brand has nothing to anchor to and the API runs
 * no such guard there, so neither does this page.
 */
export function giftcardRateChangeIsSane(
  previous: { readonly numerator: bigint; readonly denominator: bigint },
  next: { readonly numerator: bigint; readonly denominator: bigint },
): boolean {
  const nextOverPrev = next.numerator * previous.denominator;
  const prevOverNext = previous.numerator * next.denominator;
  const tooHigh = nextOverPrev >= prevOverNext * GIFTCARD_RATE_MAX_CHANGE_FACTOR;
  const tooLow = nextOverPrev * GIFTCARD_RATE_MAX_CHANGE_FACTOR <= prevOverNext;
  return !tooHigh && !tooLow;
}
